import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { DataGrid, Toolbar, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';

import { ToolBarSearch } from '../../components/datagrid';
import SvgColor from '../../components/svg-color';
import { AssignmentStatus, Status } from '../../components/request';
import type { RequestProps } from '../../models';
import {
	useAssignRequestMutation,
	useGetRequestAssignmentQuery,
	useUnassignRequestMutation,
} from '../../slices/apis/requestApi';
import {
	useAssignBulkRequestMutation,
	useGetBulkRequestAssignmentQuery,
	useUnassignBulkRequestMutation,
} from '../../slices/apis/bulkRequestApi';
import { useGetUsersQuery } from '../../slices/apis/userApi';
import type { GetRequestsResponseData, GetBulkRequestsResponseData } from '../../slices/apis/apis.types';
import { fDateTime } from '../../utils/formatTime';
import { useToast } from '../../hooks/useToast';

type Assignment = { id: number; label: string } | null;
type Assignee = NonNullable<Assignment>;
type AllocationStatus = 'Pending' | 'Assigned';
type RequestMode = 'single' | 'bulk';

type RequestRow = {
	id: number;
	candidateName: string;
	user: string;
	employer: string;
	company: string;
	testCentre: string;
	assessmentDate: string;
	assignee: Assignment;
	allocationStatus: AllocationStatus;
	status: RequestProps['status'];
};

const unassigned = '-- assign to --';
const scheduleStatuses: RequestProps['status'][] = ['READY_FOR_SCHEDULE', 'READY_FOR_ASSESSMENT'];
const tabSx = {
	minHeight: 0,
	textTransform: 'none',
	// borderRadius: 999,
	px: 2,
	py: 0.75,
	color: 'text.secondary',
	'&.Mui-selected': {
		bgcolor: 'background.paper',
		color: 'primary.main',
		boxShadow: 1,
	},
};

function AssigneeSelect({
	value,
	users,
	onChange,
}: {
	value: Assignment;
	users: Assignee[];
	onChange: (value: Assignment) => void;
}) {
	return (
		<Select
			fullWidth
			displayEmpty
			value={value?.id ?? ''}
			onChange={(event) => onChange(users.find((user) => user?.id === Number(event.target.value)) ?? null)}
			size="small"
			variant="standard"
			aria-label="Assign request"
			renderValue={() => value?.label || unassigned}
		>
			<MenuItem value="">{unassigned}</MenuItem>
			{users.map((user) => (
				<MenuItem key={user.id} value={user.id}>
					{user.label}
				</MenuItem>
			))}
		</Select>
	);
}

export default function RequestAssignment() {
	const [mode, setMode] = useState<RequestMode>('single');
	const [rows, setRows] = useState<RequestRow[]>([]);
	const theme = useTheme();

	const {
		data: singleData,
		isFetching: isSingleFetching,
		refetch: refetchSingle,
	} = useGetRequestAssignmentQuery(
		{
			paginationModel: { page: 0, pageSize: 100 },
			filterModel: {
				quickFilterValues: scheduleStatuses,
				quickFilterLogicOperator: 'or',
			},
		},
		{ skip: mode !== 'single' },
	);
	const {
		data: bulkData,
		isFetching: isBulkFetching,
		refetch: refetchBulk,
	} = useGetBulkRequestAssignmentQuery(undefined, { skip: mode !== 'bulk' });

	const isFetching = mode === 'single' ? isSingleFetching : isBulkFetching;
	const refetch = mode === 'single' ? refetchSingle : refetchBulk;

	const { data: users = [] } = useGetUsersQuery({ role: 'CONTRACTOR' });
	const [assignRequest] = useAssignRequestMutation();
	const [unassignRequest] = useUnassignRequestMutation();
	const [assignBulkRequest] = useAssignBulkRequestMutation();
	const [unassignBulkRequest] = useUnassignBulkRequestMutation();
	const { showToast, ToastElement } = useToast();
	const assigneeOptions = users.map((user) => ({
		id: user.id,
		label: `${user.firstname} ${user.lastname} (${user.email})`,
	}));
	const [selection, setSelection] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
	const [bulkAssignee, setBulkAssignee] = useState<Assignment>(null);
	const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
	const selectedIds =
		selection.type === 'include'
			? Array.from(selection.ids).map(Number)
			: rows.filter((row) => !selection.ids.has(row.id)).map((row) => row.id);

	useEffect(() => {
		if (mode === 'single') {
			setRows(singleData ? singleData.data.map(toRequestRow) : []);
		} else {
			const bulkRows = (bulkData ?? [])
				.filter((request) => scheduleStatuses.includes(request.status))
				.map(toBulkRequestRow);
			setRows(bulkRows);
		}
	}, [mode, singleData, bulkData]);

	useEffect(() => {
		setSelection({ type: 'include', ids: new Set() });
	}, []);

	const updateAssignee = async (id: number, assignee: Assignment) => {
		if (!assignee) {
			try {
				if (mode === 'single') {
					await unassignRequest({ requestIds: [id] }).unwrap();
				} else {
					await unassignBulkRequest({ requestIds: [id] }).unwrap();
				}
				await refetch();
				showToast('Assignment cancelled.', 'success');
			} catch {
				showToast('Failed to cancel assignment. Please try again.', 'error');
			}
			return;
		}
		try {
			if (mode === 'single') {
				await assignRequest({ requestIds: [id], userId: assignee.id }).unwrap();
			} else {
				await assignBulkRequest({ requestIds: [id], userId: assignee.id }).unwrap();
			}
			await refetch();
			showToast('Request assigned successfully.', 'success');
		} catch {
			showToast('Failed to assign request. Please try again.', 'error');
		}
	};

	const openBulkDialog = () => {
		setBulkAssignee(null);
		setBulkDialogOpen(true);
	};

	const confirmBulkAssignment = async () => {
		if (!bulkAssignee) return;
		try {
			if (mode === 'single') {
				await assignRequest({ requestIds: selectedIds, userId: bulkAssignee.id }).unwrap();
			} else {
				await assignBulkRequest({ requestIds: selectedIds, userId: bulkAssignee.id }).unwrap();
			}
			await refetch();
			showToast(
				`${selectedIds.length} request${selectedIds.length === 1 ? '' : 's'} assigned to ${bulkAssignee.label}`,
				'success',
			);
			setSelection({ type: 'include', ids: new Set() });
			setBulkDialogOpen(false);
		} catch {
			showToast('Failed to assign requests. Please try again.', 'error');
		}
	};

	const idColumn: GridColDef<RequestRow> = { field: 'id', headerName: 'ID', minWidth: 105, flex: 0.45 };
	const userColumn: GridColDef<RequestRow> = {
		field: 'user',
		headerName: 'User',
		minWidth: 120,
		sortable: false,
		flex: 0.65,
	};
	const assessmentDateColumn: GridColDef<RequestRow> = {
		field: 'assessmentDate',
		headerName: 'Assessment Date',
		minWidth: 165,
		flex: 0.9,
		renderCell: (params) => (
			<Typography
				color="primary"
				variant="subtitle2"
				sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}
			>
				{params.value || 'Pending'}
			</Typography>
		),
	};
	const assigneeColumn: GridColDef<RequestRow> = {
		field: 'assignee',
		headerName: 'Assignee',
		minWidth: 225,
		flex: 1.15,
		sortable: false,
		renderCell: (params) => (
			<AssigneeSelect
				value={params.value}
				users={assigneeOptions}
				onChange={(assignee) => updateAssignee(params.row.id, assignee)}
			/>
		),
	};
	const allocationStatusColumn: GridColDef<RequestRow> = {
		field: 'allocationStatus',
		headerName: 'Allocation Status',
		minWidth: 150,
		flex: 0.8,
		sortable: false,
		renderCell: (params) => {
			const allocationStatus = params.value || (params.row.assignee ? 'Assigned' : 'Pending');

			return <AssignmentStatus status={allocationStatus === 'Pending' ? 'pending' : 'assigned'} />;
		},
	};
	const statusColumn: GridColDef<RequestRow> = {
		field: 'status',
		headerName: 'Request Status',
		minWidth: 180,
		flex: 0.9,
		renderCell: (params) => <Status status={params.value} />,
	};

	const candidateNameColumn: GridColDef<RequestRow> = {
		field: 'candidateName',
		headerName: 'Name',
		minWidth: 145,
		flex: 0.75,
	};
	const employerColumn: GridColDef<RequestRow> = {
		field: 'employer',
		headerName: 'Employer',
		minWidth: 190,
		sortable: false,
		flex: 1,
	};
	const testCentreColumn: GridColDef<RequestRow> = {
		field: 'testCentre',
		headerName: 'Test Centre',
		minWidth: 120,
		sortable: false,
		flex: 0.7,
	};

	const companyColumn: GridColDef<RequestRow> = {
		field: 'company',
		headerName: 'Company',
		minWidth: 160,
		sortable: false,
		flex: 0.7,
	};
	const locationColumn: GridColDef<RequestRow> = {
		field: 'testCentre',
		headerName: 'Location',
		minWidth: 150,
		sortable: false,
		flex: 1,
	};

	const singleColumns: GridColDef<RequestRow>[] = [
		idColumn,
		candidateNameColumn,
		userColumn,
		employerColumn,
		testCentreColumn,
		assessmentDateColumn,
		assigneeColumn,
		allocationStatusColumn,
		statusColumn,
	];

	const bulkColumns: GridColDef<RequestRow>[] = [
		idColumn,
		userColumn,
		companyColumn,
		locationColumn,
		assessmentDateColumn,
		assigneeColumn,
		allocationStatusColumn,
		statusColumn,
	];

	const columns = mode === 'single' ? singleColumns : bulkColumns;

	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			<Stack spacing={3} sx={{ flex: 1, minHeight: 0 }}>
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
						alignItems: 'center',
						gap: 2,
					}}
				>
					<Box>
						<Button variant="contained" disabled={!selectedIds.length} onClick={openBulkDialog}>
							Bulk assign{selectedIds.length ? ` (${selectedIds.length})` : ''}
						</Button>
					</Box>

					<Tabs
						value={mode}
						onChange={(_event, value: RequestMode) => setMode(value)}
						TabIndicatorProps={{ sx: { display: 'none' } }}
						sx={{
							justifySelf: 'center',
							minHeight: 0,
							bgcolor: 'grey.100',
							'& .MuiTabs-flexContainer': { gap: 0.5 },
						}}
					>
						<Tab
							value="single"
							label="Single Requests"
							icon={<SvgColor src="/assets/icons/navbar/ic_request.svg" sx={{ width: 20, height: 20 }} />}
							iconPosition="start"
							disableRipple
							sx={tabSx}
						/>
						<Tab
							value="bulk"
							label="Bulk Requests"
							icon={<SvgColor src="/assets/icons/navbar/ic_bulk_request.svg" sx={{ width: 20, height: 20 }} />}
							iconPosition="start"
							disableRipple
							sx={tabSx}
						/>
					</Tabs>
				</Box>

				<Box sx={{ flex: 1, minHeight: 0, width: '100%', bgcolor: 'background.paper', borderRadius: 1 }}>
					<DataGrid
						rows={rows}
						columns={columns}
						loading={isFetching}
						checkboxSelection
						disableColumnFilter
						disableDensitySelector
						disableColumnSelector
						disableRowSelectionOnClick
						showToolbar
						slots={{
							toolbar: () => (
								<Toolbar>
									<Box sx={{ pl: 1, marginRight: 'auto' }}>
										<ToolBarSearch />
									</Box>
								</Toolbar>
							),
						}}
						rowSelectionModel={selection}
						onRowSelectionModelChange={setSelection}
						pageSizeOptions={[5]}
						initialState={{ pagination: { paginationModel: { pageSize: 20, page: 0 } } }}
						sx={{
							width: '100%',
							borderRadius: 3,
							'& .MuiDataGrid-columnHeaderTitle': {
								whiteSpace: 'normal',
								lineHeight: 1.4,
								fontSize: '0.9rem',
								color: 'grey',
							},
							'& .MuiDataGrid-columnHeader': {
								backgroundColor: theme.palette.grey[100],
							},
						}}
					/>
				</Box>
			</Stack>

			<Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Bulk assign requests</DialogTitle>
				<DialogContent>
					<FormControl fullWidth variant="standard">
						<InputLabel id="bulk-assignee-label">Assignee</InputLabel>
						<Select
							labelId="bulk-assignee-label"
							value={bulkAssignee?.id ?? ''}
							label="Assignee"
							onChange={(event) =>
								setBulkAssignee(assigneeOptions.find((user) => user.id === Number(event.target.value)) ?? null)
							}
						>
							<MenuItem value="">{unassigned}</MenuItem>
							{assigneeOptions.map((user) => (
								<MenuItem key={user.id} value={user.id}>
									{user.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 3 }}>
					<Button color="inherit" onClick={() => setBulkDialogOpen(false)}>
						Cancel
					</Button>
					<Button variant="contained" disabled={!bulkAssignee} onClick={confirmBulkAssignment}>
						Confirm assignment
					</Button>
				</DialogActions>
			</Dialog>
			{ToastElement}
		</Box>
	);
}

function toRequestRow(request: GetRequestsResponseData): RequestRow {
	return {
		id: Number(request.id),
		candidateName: request.candidateName,
		user: `${request.user.firstname} ${request.user.lastname}`,
		employer: request.candidate.employer ?? 'Pending',
		company: '—',
		testCentre: request.supplier.name,
		assessmentDate: request.assessmentDates[0] ? fDateTime(request.assessmentDates[0]) : 'Pending',
		assignee: request.assignees?.[0]
			? { id: request.assignees[0].id, label: `${request.assignees[0].firstname} ${request.assignees[0].lastname}` }
			: null,
		allocationStatus: request.assignees?.length ? 'Assigned' : 'Pending',
		status: request.status,
	};
}

function toBulkRequestRow(request: GetBulkRequestsResponseData): RequestRow {
	return {
		id: Number(request.id),
		candidateName: '—',
		user: request.user ? `${request.user.firstname} ${request.user.lastname}` : '—',
		employer: '—',
		company: request.user?.company ?? '—',
		testCentre: request.location || '—',
		assessmentDate: request.assessmentDates?.[0] ? fDateTime(request.assessmentDates[0]) : 'Pending',
		assignee: request.assignees?.[0]
			? { id: request.assignees[0].id, label: `${request.assignees[0].firstname} ${request.assignees[0].lastname}` }
			: null,
		allocationStatus: request.assignees?.length ? 'Assigned' : 'Pending',
		status: request.status,
	};
}
